import {
	BrandFileType,
	useConfirmBrandFileUploadMutation,
	useGenerateBrandFileUploadUrlMutation,
} from '@codezo/graphql';
import { ImageIcon, Upload, X } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Input } from '../../components/ui/input';
import { SelectPopover } from '../../components/ui/select-popover';
import { useFilePrepare } from '../../hooks/useFilePrepare';
import {
	BRAND_DOC_ACCEPT,
	BRAND_DOC_LABEL,
	BRAND_IMAGE_ACCEPT,
	BRAND_IMAGE_LABEL,
	FONT_ACCEPT,
	FONT_LABEL,
} from '../../lib/file-types';

const fileTypeOptions = [
	{ value: BrandFileType.Logo, label: 'Logo' },
	{ value: BrandFileType.Icon, label: 'Icon' },
	{ value: BrandFileType.Image, label: 'Image' },
	{ value: BrandFileType.Document, label: 'Document' },
	{ value: BrandFileType.Font, label: 'Font' },
];

/** Accepted files depend on the selected brand asset type. */
function acceptForBrandType(type: BrandFileType): { accept: string; label: string } {
	if (type === BrandFileType.Document)
		return { accept: BRAND_DOC_ACCEPT, label: BRAND_DOC_LABEL };
	if (type === BrandFileType.Font) return { accept: FONT_ACCEPT, label: FONT_LABEL };
	return { accept: BRAND_IMAGE_ACCEPT, label: BRAND_IMAGE_LABEL };
}

export function UploadBrandAssetModal({
	open,
	onOpenChange,
	brandId,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	brandId: string;
	onSuccess?: () => void;
}) {
	const [file, setFile] = useState<File | null>(null);
	const [preview, setPreview] = useState<string | null>(null);
	const [label, setLabel] = useState('');
	const [fileType, setFileType] = useState<BrandFileType>(BrandFileType.Logo);
	const [uploading, setUploading] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const { prepare, converting } = useFilePrepare();

	const [generateUploadUrl] = useGenerateBrandFileUploadUrlMutation();
	const [confirmUpload] = useConfirmBrandFileUploadMutation();

	const brandAccept = acceptForBrandType(fileType);

	/** Switching asset type clears a now-mismatched file. */
	const handleTypeChange = (next: BrandFileType) => {
		setFileType(next);
		setFile(null);
		setPreview(null);
	};

	const resetForm = () => {
		setFile(null);
		setPreview(null);
		setLabel('');
		setFileType(BrandFileType.Logo);
	};

	const handleFileSelect = useCallback((selected: File) => {
		setFile(selected);
		setLabel((prev) => prev || selected.name.replace(/\.[^.]+$/, ''));

		if (selected.type.startsWith('image/')) {
			const url = URL.createObjectURL(selected);
			setPreview(url);
		} else {
			setPreview(null);
		}
	}, []);

	const handleDrop = useCallback(
		async (e: React.DragEvent) => {
			e.preventDefault();
			const picked = e.dataTransfer.files[0];
			if (!picked) return;
			const [dropped] = await prepare([picked], acceptForBrandType(fileType).accept);
			if (dropped) handleFileSelect(dropped);
		},
		[handleFileSelect, prepare, fileType]
	);

	const handleSubmit = async () => {
		if (!file || !label.trim()) return;
		setUploading(true);

		/* Keep the real extension on the stored name. It's what the format badge
		 * and the file-type icon read, and the backend falls back to it when the
		 * browser reports a vague mime type — browsers routinely send .ai as
		 * application/octet-stream — so dropping it can cost the file its
		 * server-rendered thumbnail. */
		const trimmedLabel = label.trim();
		const ext = file.name.match(/\.([a-z0-9]{1,5})$/i)?.[1]?.toLowerCase() ?? '';
		const storedName =
			ext && !trimmedLabel.toLowerCase().endsWith(`.${ext}`)
				? `${trimmedLabel}.${ext}`
				: trimmedLabel;

		try {
			const { data: urlData } = await generateUploadUrl({
				variables: {
					input: {
						brandId,
						brandFileType: fileType,
						fileName: file.name,
						fileSize: file.size,
						fileType: file.type,
					},
				},
			});

			const uploadUrl = urlData?.generateBrandFileUploadUrl?.uploadUrl;
			const blobUrl = urlData?.generateBrandFileUploadUrl?.blobUrl;
			const fileId = urlData?.generateBrandFileUploadUrl?.fileId;

			if (!uploadUrl || !blobUrl || !fileId) {
				throw new Error('Failed to generate upload URL');
			}

			await fetch(uploadUrl, {
				method: 'PUT',
				headers: {
					'x-ms-blob-type': 'BlockBlob',
					'Content-Type': file.type,
				},
				body: file,
			});

			await confirmUpload({
				variables: {
					input: {
						brandId,
						fileId,
						blobUrl,
						brandFileType: fileType,
						fileName: storedName,
						fileSize: file.size,
						fileType: file.type,
					},
				},
			});

			toast.success('Asset uploaded');
			resetForm();
			onOpenChange(false);
			onSuccess?.();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Upload failed');
		} finally {
			setUploading(false);
		}
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next) resetForm();
				onOpenChange(next);
			}}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Upload asset</DialogTitle>
					<DialogDescription>
						Upload a logo variant, icon, image, document or font.
					</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-5">
							{/* Drop zone */}
							<div
								onDragOver={(e) => e.preventDefault()}
								onDrop={handleDrop}
								onClick={() => inputRef.current?.click()}
								className="flex cursor-pointer flex-col items-center gap-3 rounded-xl border-2 border-dashed border-line-1 bg-surface-fill/50 px-4 py-8 text-center transition-colors hover:border-line-2 hover:bg-surface-hover"
							>
								{preview ? (
									<div className="relative">
										<img
											src={preview}
											alt="Preview"
											className="max-h-24 rounded-lg border border-line-1 object-contain"
										/>
										<button
											type="button"
											onClick={(e) => {
												e.stopPropagation();
												setFile(null);
												setPreview(null);
											}}
											className="absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full bg-surface text-text-3 shadow-sm ring-1 ring-line-1 hover:text-text-1"
										>
											<X size={10} />
										</button>
									</div>
								) : file ? (
									<div className="flex items-center gap-2">
										<ImageIcon size={16} className="text-text-3" />
										<span className="text-sm text-text-2">{file.name}</span>
										<button
											type="button"
											onClick={(e) => {
												e.stopPropagation();
												setFile(null);
											}}
											className="text-text-4 hover:text-text-1"
										>
											<X size={12} />
										</button>
									</div>
								) : (
									<>
										<div className="flex h-10 w-10 items-center justify-center rounded-full border border-line-1 bg-surface text-text-3">
											<Upload size={18} />
										</div>
										<div>
											<div className="text-sm font-medium text-text-2">
												{converting
													? 'Converting…'
													: 'Drop a file here or click to browse'}
											</div>
											<div className="mt-1 text-xs text-text-4">
												{brandAccept.label}
											</div>
										</div>
									</>
								)}
								<input
									ref={inputRef}
									type="file"
									accept={brandAccept.accept}
									className="hidden"
									onChange={async (e) => {
										const picked = e.target.files?.[0];
										e.target.value = '';
										if (picked) {
											const [f] = await prepare(
												[picked],
												acceptForBrandType(fileType).accept
											);
											if (f) handleFileSelect(f);
										}
									}}
								/>
							</div>

							<Field label="Asset type" required>
								<SelectPopover
									value={fileType}
									onChange={(v) => handleTypeChange(v as BrandFileType)}
									options={fileTypeOptions}
									placeholder="Select type"
								/>
							</Field>

							<Field label="Label" required>
								<Input
									value={label}
									onChange={(e) => setLabel(e.target.value)}
									placeholder="e.g. Primary mark"
								/>
							</Field>
						</div>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => {
								resetForm();
								onOpenChange(false);
							}}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!file || !label.trim() || uploading}>
							{uploading ? 'Uploading...' : 'Upload'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
